import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/reducers/state';
import { useMonetizationActions } from 'src/store/monetization/actions';
import { getFormattedDate, changeDateFormatterForVideoList } from 'src/components/utilities/utils';
import Loader from 'src/components/loader';
import ToastMessage from 'src/components/ToastContainer';
import 'react-toastify/dist/ReactToastify.css';
import { useParams } from 'react-router-dom';
import Pagination from 'src/components/common/pagination';
const VoucherUsage: React.FC = () => {
  const dispatch = useDispatch();
  const { id } = useParams<string>();
  const monetizationAction = useMonetizationActions(dispatch);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsageList, setFilteredUsageList] = useState<any[]>([]);
  const { voucherUsageList, isVoucherUsageLoaded, paginationMeta } = useSelector((state: RootState) => {
    return {
      voucherUsageList: state.monetization.voucherUsageList,
      isVoucherUsageLoaded: state.monetization.isVoucherUsageLoaded,
      paginationMeta: state.monetization.paginationMeta,
    };
  });

  useEffect(() => {
    id && monetizationAction.getVoucherUsage({ id, pageNumber: 1, pageSize: 20 });
  }, []);

  useEffect(() => {
    if (voucherUsageList && voucherUsageList.length > 0) {
      const filtered = voucherUsageList.filter((usage: any) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          usage.name?.toLowerCase().includes(searchLower) ||
          usage.email?.toLowerCase().includes(searchLower) ||
          usage.voucherCode?.toLowerCase().includes(searchLower) ||
          usage.voucherName?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredUsageList(filtered);
    } else {
      setFilteredUsageList([]);
    }
  }, [searchTerm, voucherUsageList]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const convertToCSV = (data: any[]) => {
    if (!data || data.length === 0) return '';

    const headers = [
      'Ref No.',
      'Voucher Code',
      'Voucher Name',
      'Customer Name',
      'Email',
      'Amount Used',
      'Used Date'
    ];

    const rows = data.map((usage) => {
      return [
        usage.refNo || '',
        usage.voucherCode || '',
        usage.voucherName || '',
        `${usage.firstName || ''} ${usage.lastName || ''}`.trim(),
        usage.email || '',
        usage.amount || '0',
        changeDateFormatterForVideoList(usage.usedDate) || ''
      ].map((field) => {
        const fieldStr = String(field);
        if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
          return `"${fieldStr.replace(/"/g, '""')}"`;
        }
        return fieldStr;
      });
    });

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    return csvContent;
  };

  const handleExport = () => {
    if (!filteredUsageList || filteredUsageList.length === 0) {
      alert('No data to export');
      return;
    }

    try {
      const csvContent = convertToCSV(filteredUsageList);

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `voucher-usage-${currentDate}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export data. Please try again.');
    }
  };
  const handlePageChange = (pageNumber: number) => {
    id && monetizationAction.getVoucherUsage({ id, pageNumber, pageSize: 20 });
  };
  return (
    <>
      <ToastMessage />
      <div className="mt-4">
        <div className="flex flex-col justify-center h-full">
          <div className="w-full mx-auto bg-white shadow-card rounded-lg relative">
            <div className="px-5 py-4 border-b border-gray-100 md:flex justify-between items-center">
              <h2 className="font-semibold text-xl">Voucher Usage</h2>
              <div className="md:flex items-center text-center gap-2">
                <div className="form-group md:flex items-center text-center">
                  <label className="mr-1 font-semibold">Search:</label>
                  <input
                    onChange={handleSearch}
                    className="input-style w-48 mr-3 mb-1"
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                  />
                </div>
                <button
                  onClick={handleExport}
                  type="button"
                  disabled={!filteredUsageList || filteredUsageList.length === 0}
                  className="btn-primary w-auto whitespace-nowrap mt-0 mb-1 disabled:opacity-50 disabled:cursor-not-allowed">
                  Export
                </button>
              </div>
            </div>

            <div className="p-3">
              <div className="overflow-x-auto-sm">
                <table className="table-auto w-full border">
                  <thead className="text-xs font-semibold uppercase bg-gray-50">
                    <tr>
                      <th className="p-2">
                        <div className="font-semibold text-left">Ref No.</div>
                      </th>
                      <th className="p-2">
                        <div className="font-semibold text-left">Voucher Code</div>
                      </th>
                      <th className="p-2">
                        <div className="font-semibold text-left">Voucher Name</div>
                      </th>
                      <th className="p-2">
                        <div className="font-semibold text-left">Customer</div>
                      </th>
                      <th className="p-2">
                        <div className="font-semibold text-left">Email</div>
                      </th>
                      <th className="p-2">
                        <div className="font-semibold text-left">Amount Used</div>
                      </th>
                      <th className="p-2">
                        <div className="font-semibold text-left">Used Date</div>
                      </th>
                    </tr>
                  </thead>
                  {isVoucherUsageLoaded ? (
                    <tbody>
                      <tr>
                        <td colSpan={7}>
                          <Loader />
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <>
                      {filteredUsageList && filteredUsageList.length > 0 ? (
                        <tbody className="text-sm divide-y divide-gray-100">
                          {filteredUsageList.map((usage, index) => {
                            return (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="p-2">
                                  <div className="flex items-center">
                                    <div className="font-medium">{usage.refNo}</div>
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="text-left">
                                    <span className="inline-block px-2 py-1 text-xs font-semibold rounded-md bg-blue-100 text-blue-800">
                                      {usage.voucherCode}
                                    </span>
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="text-left">{usage.voucherName}</div>
                                </td>
                                <td className="p-2">
                                  <div className="text-left">
                                    {usage.firstName} {usage.lastName}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="text-left">{usage.email}</div>
                                </td>
                                <td className="p-2">
                                  <div className="text-left font-medium">${usage.amount}</div>
                                </td>
                                <td className="p-2">
                                  <div className="text-left">
                                    {changeDateFormatterForVideoList(usage.usedDate)}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      ) : (
                        <tbody>
                          <tr>
                            <td className="text-center py-8" colSpan={7}>
                              <div className="flex flex-col items-center justify-center">
                                <svg
                                  className="w-16 h-16 text-gray-300 mb-2"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                </svg>
                                <p className="text-gray-500 font-medium">
                                  No voucher usage data found
                                </p>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      )}
                    </>
                  )}
                </table>
                {!!paginationMeta && !!Object.keys(paginationMeta).length && (
                  <Pagination
                    currentPage={paginationMeta.pageNumber}
                    totalCount={paginationMeta.totalCount}
                    pageSize={paginationMeta.pageSize}
                    onPageChange={(page: any) => handlePageChange(page)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VoucherUsage;
